import { DefaultRootState } from '@databases/redux';
import {
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { ExportFileType, RenderContext } from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import { fetchConfluenceData } from '@databases/utils/confluence/fetch';
import { copyHtmlToClipboard } from '@databases/utils/copy-paste';
import {
  exportDatabase,
  exportDatabaseToHtml,
  exportEntryToHtml,
  exportSelectedContentToHtml,
} from '@databases/utils/database/export';
import { TranslatableError } from '@databases/utils/errors';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdRequired,
} from '@databases/utils/redux/databases/selectors';
import { captureException } from '@sentry/react';

export const isSafari = () =>
  navigator.userAgent.toLowerCase().includes('safari') &&
  !navigator.userAgent.toLowerCase().includes('chrome');

export function downloadFile(file: File): void {
  const fileURL = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = fileURL;
  link.download = file.name;
  link.click();
}

export async function exportAndDownloadDatabase(
  {
    contentId,
    exportType,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional,
  }: {
    contentId: string;
    exportType: ExportFileType;
    viewSettings?: DatabaseViewSettings;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): Promise<void> {
  switch (exportType) {
    case ExportFileType.HTML:
    case ExportFileType.CSV:
      downloadFile(
        await exportDatabase(
          { contentId, exportType, viewSettings, selectDatabase },
          state
        )
      );
      break;

    case ExportFileType.PDF:
      await openPrintDatabasePopup({ contentId, viewSettings, selectDatabase }, state);
      break;

    default:
      throw new TranslatableError(
        `Unsupported export type ${exportType}.`,
        'snackbar-error-message.unsupported-export-type',
        { exportType }
      );
  }
}

const printStyle = `
    #loading {
        position: fixed;
        top: calc(50% - 25px);
        left: calc(50% - 25px);
        animation: spin 0.86s cubic-bezier(0.4, 0.15, 0.6, 0.85) infinite;
    }

    @keyframes spin {
        100% {
            transform: rotate(360deg);
        }
    }
`;

export async function openPrintDatabasePopup(
  {
    contentId,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional,
  }: {
    contentId: string;
    viewSettings?: DatabaseViewSettings;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): Promise<void> {
  await openPrintPopup(
    async () =>
      await exportDatabaseToHtml(
        {
          contentId,
          renderContext: RenderContext.PRINT_PREVIEW,
          viewSettings,
          selectDatabase,
        },
        state
      ),
    state
  );
}

export async function openPrintEntryPopup(
  {
    contentId,
    entryId,
    selectDatabase = selectDatabaseByContentIdOptional,
  }: {
    contentId: string;
    entryId: string;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): Promise<void> {
  await openPrintPopup(
    async () =>
      await exportEntryToHtml(
        {
          contentId,
          entryId,
          renderContext: RenderContext.PRINT_PREVIEW,
          selectDatabase,
        },
        state
      ),
    state
  );
}

async function openPrintPopup(
  getHtml: () => Promise<string>,
  state: DefaultRootState
): Promise<void> {
  const popup = open(
    undefined,
    undefined,
    `popup=1,height=${screen.height},width=${Math.min(screen.width, 1100)}`
  );
  if (!popup) {
    throw new TranslatableError(
      'Could not open popup window.',
      'snackbar-error-message.could-not-open-popup-window'
    );
  }

  const styleEl = popup.document.createElement('style');
  styleEl.textContent = printStyle;
  popup.document.head.appendChild(styleEl);

  const spinner = popup.document.createElement('div');
  spinner.innerHTML =
    '<svg id="loading" height="50" viewbox="0 0 50 50" width="50" xmlns="http://www.w3.org/2000/svg"><circle class="spin-circle" cx="25" cy="25" fill="none" r="22.5" stroke-linecap="round" stroke-width="5" style="stroke: #42526e; transform-origin: center; transition: stroke-dashoffset 0.8s ease-in-out, opacity 0.2s ease-in-out; transition-delay: initial; stroke-dashoffset: 113.097px; stroke-dasharray: 141.371px;"></circle></svg>';
  popup.document.body.appendChild(spinner);

  const htmlExport = await getHtml();

  // Load export view into temporary document to prevent images from being loaded (so that we can replace them by data URLs first)
  const tempDocument = new DOMParser().parseFromString(htmlExport, 'text/html');

  const stylesP = Promise.all(
    [...popup.document.importNode(tempDocument.head, true).childNodes].map(
      async (node) => {
        popup.document.head.appendChild(node);
        if (
          ['[object HTMLStyleElement]', '[object HTMLLinkElement]'].includes(`${node}`) &&
          (node as HTMLElement).matches('style,link[rel=stylesheet]')
        ) {
          await new Promise((resolve) => {
            node.addEventListener('load', resolve);
            node.addEventListener('error', resolve);
          });
        }
      }
    )
  );

  const baseUrl = selectBaseUrl(state);

  const images = [...tempDocument.body.querySelectorAll('img')];
  await Promise.all(
    images.map(async (img) => {
      if (img.src.startsWith(baseUrl)) {
        try {
          // TODO: Use attachments reducer
          const data = await fetchConfluenceData(img.src.substring(baseUrl.length)).then(
            (res) => res.blob()
          );
          const dataUrl = ((popup as any).URL as typeof URL).createObjectURL(data);
          img.src = dataUrl;
        } catch (err) {
          ((popup as any).console as typeof console).warn('Could not load image', err);
          captureException(err);
        }
      }
    })
  );

  await stylesP;

  spinner.remove();
  for (const node of popup.document.importNode(tempDocument.body, true).childNodes) {
    popup.document.body.appendChild(node);
  }

  await Promise.all(
    images.map(async (img) => {
      if (!img.complete) {
        await new Promise((resolve) => {
          img.addEventListener('load', resolve);
          img.addEventListener('error', resolve);
        });
      }
    })
  );

  await new Promise<void>((resolve) => {
    // Somehow the blob: images are complete immediately and don't fire a load/error event, but they are missing from the
    // print if we don't add a small delay.
    popup.setTimeout(() => {
      popup.print();
      resolve();
    }, 250);
  });
}

export async function copyDatabaseToClipboard(
  {
    contentId,
    viewSettings,
    selectDatabase = selectDatabaseByContentIdOptional,
  }: {
    contentId: string;
    viewSettings?: DatabaseViewSettings;
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
  },
  state: DefaultRootState
): Promise<void> {
  const html = await exportDatabaseToHtml(
    {
      contentId,
      renderContext: RenderContext.COPY_DATABASE_TO_CLIPBOARD,
      viewSettings,
      selectDatabase,
    },
    state
  );
  copyHtmlToClipboard(html);
}

export const doExportContent = async (
  contentId: string,
  scope: string,
  viewSettings: DatabaseViewSettings | undefined,
  state: DefaultRootState
) => {
  const selectDatabase = selectDatabaseByContentIdRequired;
  const selectedFieldIds = selectSelectedFieldIds(state, {
    contentId,
    scope,
    viewSettings,
  });
  const selectedEntryIds = selectSelectedEntryIds(state, {
    contentId,
    scope,
    viewSettings,
  });

  const html = await exportSelectedContentToHtml(
    {
      contentId,
      renderContext: RenderContext.COPY_DATABASE_TO_CLIPBOARD,
      selectDatabase,
      selectedFieldIds,
      selectedEntryIds,
    },
    state
  );
  copyHtmlToClipboard(html);
};
